import { isBlockedHost } from './egress.js';

export type EndpointValidation = { ok: true; url: string } | { ok: false; reason: string };

const REASON_EMPTY = 'Enter the MCP server URL.';
const REASON_UNPARSEABLE = 'Enter a valid URL, for example https://mcp.example.com/mcp.';
const REASON_SCHEME_STRICT = 'Use an https:// URL.';
const REASON_SCHEME_INSECURE_OK = 'Use an http:// or https:// URL.';
const REASON_CREDENTIALS = 'Remove the username and password from the URL.';
const REASON_FRAGMENT = 'Remove the fragment (the part after #) from the URL.';
const REASON_UNREACHABLE = 'That address is not publicly reachable. Enter a public MCP server URL.';

/**
 * Validates a customer-supplied MCP endpoint before Parcel ever contacts it.
 * `allowInsecure` (local dev only) relaxes the scheme check; the host blocklist always applies.
 */
export function validateCustomEndpoint(
  input: string,
  options: { allowInsecure?: boolean } = {},
): EndpointValidation {
  const trimmed = input.trim();
  if (!trimmed) return { ok: false, reason: REASON_EMPTY };

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { ok: false, reason: REASON_UNPARSEABLE };
  }

  const schemeOk = options.allowInsecure
    ? parsed.protocol === 'https:' || parsed.protocol === 'http:'
    : parsed.protocol === 'https:';
  if (!schemeOk) {
    return {
      ok: false,
      reason: options.allowInsecure ? REASON_SCHEME_INSECURE_OK : REASON_SCHEME_STRICT,
    };
  }

  if (parsed.username || parsed.password) {
    return { ok: false, reason: REASON_CREDENTIALS };
  }

  if (parsed.hash) {
    return { ok: false, reason: REASON_FRAGMENT };
  }

  // isBlockedHost strips a trailing dot itself now; this strip is for canonical output and to
  // catch a degenerate all-dot host, which normalizes to '' and matches no blocklist pattern.
  const hostname = parsed.hostname.replace(/\.+$/, '');
  if (!hostname || isBlockedHost(hostname)) {
    return { ok: false, reason: REASON_UNREACHABLE };
  }

  parsed.hostname = hostname;
  return { ok: true, url: parsed.toString() };
}
